import "./ChatMessage.scss";

import {
  Box,
  Button,
  IconButton,
  Paper,
  TextareaAutosize
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import {
  handleRegenerateMessage,
  handleResubmitMessage,
  setFeedbackDialogState,
  setMessageRating,
  setSelectedMessageIndex
} from "../redux/chatSlice";
import { messageRated, messageRegenerated } from "../segment/Events";
import { useDispatch, useSelector } from "react-redux";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import ReactMarkdown from "react-markdown";
import RefreshIcon from "@mui/icons-material/Refresh";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import { handleCopy } from "../utils/Functions";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";

const ChatMessage = ({ message }) => {
  const dispatch = useDispatch();
  const bot = useSelector((state) => state.chat.bot);
  const isLoading = useSelector((state) => state.chat.isLoading);

  const [isEditing, setIsEditing] = useState(false);
  const [editedMessage, setEditedMessage] = useState(message.message);
  const textareaRef = useRef(null);

  const handleEditSubmit = () => {
    setIsEditing(false);
    if (editedMessage.length > 0) {
      const updatedMessage = {
        ...message,
        message: editedMessage
      };
      dispatch(handleResubmitMessage(updatedMessage));
    }
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditedMessage(message.message);
  };

  const handleRegenerate = () => {
    dispatch(handleRegenerateMessage(message.index));
    messageRegenerated();
  };

  const handleFeedback = (positive) => {
    dispatch(setSelectedMessageIndex(message.index));
    dispatch(
      setMessageRating({
        positive,
        messageIndex: message.index
      })
    );
    dispatch(setFeedbackDialogState({ open: true, positive }));
    messageRated(positive, message.message);
  };

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditing]);

  return (
    <Box className={`message ${message.isAI ? "message-ai" : "message-user"}`}>
      {message.isAI && (
        <img
          src={bot.logo}
          alt={`${bot.name} Logo`}
          className="message-ai-logo"
        />
      )}
      {!message.isAI && !isEditing && !isLoading && (
        <IconButton
          onClick={() => setIsEditing(true)}
          style={{ color: "#7d7f85", fontSize: "small" }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
      <Box
        className={`message-container ${
          isEditing ? "message-containerEditing" : ""
        }`}
      >
        {isEditing ? (
          <>
            <TextareaAutosize
              className="message-editInput"
              ref={textareaRef}
              value={editedMessage}
              onChange={(e) => setEditedMessage(e.target.value)}
            />
            <Box
              className="message-editButtons"
              display="flex"
              justifyContent="flex-end"
              mt={1}
            >
              <Button
                className="message-editButton message-editCancel"
                variant="outlined"
                onClick={handleEditCancel}
                sx={{ mr: 1 }}
              >
                Cancel
              </Button>
              <Button
                className="message-editButton message-editSend"
                variant="contained"
                onClick={handleEditSubmit}
              >
                Send
              </Button>
            </Box>
          </>
        ) : (
          <Paper elevation={3} className="message-bubble">
            <div className="message-scroll">
              <ReactMarkdown
                className="message-text"
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
              >
                {message.message}
              </ReactMarkdown>
            </div>
          </Paper>
        )}
        {message.isAI && (
          <Box className="message-aiButtons">
            <IconButton
              className="message-aiButton"
              onClick={() => handleCopy(message.message)}
            >
              <ContentCopyIcon />
            </IconButton>
            {message.feedback?.positive === true && (
              <IconButton className="message-aiButton" disabled>
                <ThumbUpIcon />
              </IconButton>
            )}
            {message.feedback?.positive === undefined && (
              <IconButton
                className="message-aiButton"
                onClick={() => handleFeedback(true)}
              >
                <ThumbUpOutlinedIcon />
              </IconButton>
            )}
            {message.feedback?.positive === false && (
              <IconButton className="message-aiButton" disabled>
                <ThumbDownIcon />
              </IconButton>
            )}
            {message.feedback?.positive === undefined && (
              <IconButton
                className="message-aiButton"
                onClick={() => handleFeedback(false)}
              >
                <ThumbDownOutlinedIcon />
              </IconButton>
            )}

            <IconButton className="message-aiButton" onClick={handleRegenerate}>
              <RefreshIcon />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChatMessage;
